import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../config/frontend';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  Calendar,
  RefreshCw,
  Loader2,
  CreditCard,
  Package,
  UserCheck,
  DollarSign,
  Building2,
  Home,
  ShoppingCart,
  BarChart3,
  Receipt,
  Layers,
  Tag,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Printer,
  Calculator,
  FileText,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { toast } from 'react-hot-toast';

const SupplierPayments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency, currencySymbol } = useSettings();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI State

  // Suppliers and payments data
  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentStats, setPaymentStats] = useState({
    total_payments: 0,
    total_amount: 0,
    pending_payments: 0,
    completed_payments: 0
  });

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    payment_method: 'all',
    date_range: 'all',
    sort_by: 'created_at'
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 15,
    totalPages: 1,
    totalItems: 0
  });

  // Form states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    company_name: 'NEXAPOS',
    company_address: '123 Business Street, Accra',
    company_phone: '+233 24 123 4567',
    tax_number: '',
    receipt_footer: 'Thank you for doing business with us! \nKeep this receipt for your records. \nPowered By SherSoft Technologies - 0548104944'
  });
  const [paymentForm, setPaymentForm] = useState({
    supplier_id: '',
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    notes: '',
    payment_date: new Date().toISOString().split('T')[0],
    paymentID: '', // Auto-generated payment ID
    outstanding_balance: 0, // Supplier's outstanding balance
    // Payment method specific fields
    cash_breakdown: {},
    bank_name: '',
    account_name: '',
    account_number: '',
    mobile_network: '',
    sender_mobile_number: '',
    receiver_mobile_number: '',
    transaction_reference: '',
    card_number: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Debounced search state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Filter states
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);

  // Sort state
  const [sortBy, setSortBy] = useState('date');


  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update filters when debounced search changes
  useEffect(() => {
    if (searchInput !== debouncedSearch) {
      setIsSearching(true);
    }
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch, searchInput]);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await apiService.getSuppliers({ per_page: 1000 });
      if (response.success) {
        setSuppliers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, []);

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await apiService.getSettings();
      if (response.success) {
        setCompanySettings({
          company_name: response.data.company_name || 'NEXAPOS',
          company_address: response.data.company_address || '123 Business Street, Accra',
          company_phone: response.data.company_phone || '+233 24 123 4567',
          tax_number: response.data.tax_number || '',
          receipt_footer: response.data.receipt_footer || 'Thank you for doing business with us! \nKeep this receipt for your records. \nPowered By SherSoft Technologies - 0548104944'
        });
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getSupplierPayments({
        ...filters,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage
      });
      
      if (response.success) {
        setPayments(response.data.payments || response.data || []);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.last_page || response.last_page || 1,
          totalItems: response.data.total || response.total || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError('Failed to load payments');
      // Generate mock data for testing
      const mockPayments = generateMockPayments();
      setPayments(mockPayments);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalItems: mockPayments.length
      }));
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // Fetch payment statistics
  const fetchPaymentStats = useCallback(async () => {
    try {
      const response = await apiService.getSupplierPaymentStats();
      if (response.success) {
        setPaymentStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      // Calculate stats from payments data
      const totalPayments = payments.length;
      const totalAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const pendingPayments = payments.filter(p => p.status === 'pending').length;
      const completedPayments = payments.filter(p => p.status === 'completed').length;
      
      setPaymentStats({
        total_payments: totalPayments,
        total_amount: totalAmount,
        pending_payments: pendingPayments,
        completed_payments: completedPayments
      });
    }
  }, [payments]);

  // Generate mock payments for testing
  const generateMockPayments = () => {
    const mockSuppliers = suppliers.slice(0, 10);
    return mockSuppliers.map((supplier, index) => ({
      id: index + 1,
      supplier_id: supplier.id,
      supplier_name: supplier.name,
      supplier_email: supplier.email,
      amount: (Math.random() * 5000 + 500).toFixed(2),
      payment_method: ['cash', 'bank_transfer', 'cheque', 'mobile_money'][Math.floor(Math.random() * 4)],
      reference_number: `REF-${Date.now()}-${index}`,
      status: ['pending', 'completed', 'failed'][Math.floor(Math.random() * 3)],
      notes: `Payment for ${supplier.name}`,
      payment_date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      created_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
    }));
  };

  // Load data on component mount
  useEffect(() => {
    fetchSuppliers();
    fetchCompanySettings();
  }, [fetchSuppliers, fetchCompanySettings]);

  useEffect(() => {
    if (suppliers.length > 0) {
      fetchPayments();
    }
  }, [fetchPayments, suppliers]);

  useEffect(() => {
    if (payments.length > 0) {
      fetchPaymentStats();
    }
  }, [payments, fetchPaymentStats]);

  // Refetch data when pagination changes
  useEffect(() => {
    fetchPayments();
  }, [pagination.currentPage, pagination.itemsPerPage, fetchPayments]);


  // Ensure outstanding_balance is always a number
  useEffect(() => {
    // Ensure outstanding_balance is always a valid number
    if (typeof paymentForm.outstanding_balance !== 'number' || isNaN(paymentForm.outstanding_balance)) {
      setPaymentForm(prev => ({
        ...prev,
        outstanding_balance: 0
      }));
    }
  }, [paymentForm]);

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Filtered payments based on search and filters
  const filteredPayments = useMemo(() => {
    let filtered = payments;

    // Apply search filter
    if (searchInput) {
      filtered = filtered.filter(payment =>
        payment.supplier_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        payment.paymentID?.toLowerCase().includes(searchInput.toLowerCase()) ||
        payment.reference_number?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(payment => payment.status === filterStatus);
    }

    // Apply payment method filter
    if (filterMethod !== 'all') {
      filtered = filtered.filter(payment => payment.payment_method === filterMethod);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return parseFloat(b.amount) - parseFloat(a.amount);
        case 'date':
        default:
          return new Date(b.payment_date) - new Date(a.payment_date);
      }
    });

    return filtered;
  }, [payments, searchInput, filterStatus, filterMethod, sortBy]);

  // Update total pages when filtered payments change
  useEffect(() => {
    const total = Math.ceil(filteredPayments.length / itemsPerPage);
    setTotalPages(total);
    
    // Reset to first page if current page is beyond total pages
    if (currentPage > total && total > 0) {
      setCurrentPage(1);
    }
  }, [filteredPayments.length, itemsPerPage, currentPage]);

  // Payment form handlers
  const handlePaymentFormChange = async (field, value) => {
    setPaymentForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // If supplier is selected, fetch outstanding balance
    if (field === 'supplier_id' && value) {
      const outstandingBalance = await fetchSupplierOutstandingBalance(value);
      setPaymentForm(prev => ({
        ...prev,
        outstanding_balance: typeof outstandingBalance === 'number' ? outstandingBalance : 0
      }));
    }
  };

  // Cash breakdown update function
  const updateCashBreakdown = (denomination, count) => {
    setPaymentForm(prev => ({
      ...prev,
      cash_breakdown: {
        ...prev.cash_breakdown,
        [`${denomination}_notes`]: parseInt(count) || 0
      }
    }));
  };

  // Generate payment ID function
  const generatePaymentId = async () => {
    try {
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const response = await apiService.getNextPaymentId(today);
      if (response.success) {
        return response.data.payment_id;
      }
    } catch (error) {
      console.error('Error generating payment ID:', error);
    }
    // Fallback: generate a simple ID if API fails
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const fallbackId = `SPAY-${today}001`;
    return fallbackId;
  };

  // Fetch supplier outstanding balance
  const fetchSupplierOutstandingBalance = async (supplierId) => {
    try {
      const response = await apiService.getSupplierOutstandingBalance(supplierId);
      if (response.success) {
        const balance = response.data.outstanding_balance;
        // Handle both number and string values
        if (typeof balance === 'number') {
          return balance;
        } else if (typeof balance === 'string') {
          const parsed = parseFloat(balance);
          return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
      }
    } catch (error) {
      console.error('Error fetching supplier outstanding balance:', error);
    }
    return 0;
  };

  // Handle opening payment modal with auto-generated ID (only for new payments)
  const handleOpenPaymentModal = async () => {
    // First, generate the payment ID
    const paymentId = await generatePaymentId();
    
    // Then, set the form with the payment ID
    setPaymentForm(prev => ({
      ...prev,
      paymentID: paymentId
    }));
    
    // Finally, open the modal
    setShowPaymentModal(true);
  };

  const validatePaymentForm = () => {
    const errors = {};
    
    if (!paymentForm.supplier_id) {
      errors.supplier_id = 'Please select a supplier';
    }
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    } else if (parseFloat(paymentForm.amount) > 1000000) {
      errors.amount = 'Amount cannot exceed 1,000,000';
    }
    
    if (!paymentForm.payment_method) {
      errors.payment_method = 'Please select a payment method';
    }
    
    if (!paymentForm.payment_date) {
      errors.payment_date = 'Please select a payment date';
    } else {
      const selectedDate = new Date(paymentForm.payment_date);
      const today = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      
      if (selectedDate > today) {
        errors.payment_date = 'Payment date cannot be in the future';
      } else if (selectedDate < oneYearAgo) {
        errors.payment_date = 'Payment date cannot be more than 1 year ago';
      }
    }
    
    // Validate reference number if provided
    if (paymentForm.reference_number && paymentForm.reference_number.length > 100) {
      errors.reference_number = 'Reference number cannot exceed 100 characters';
    }
    
    // Validate notes if provided
    if (paymentForm.notes && paymentForm.notes.length > 500) {
      errors.notes = 'Notes cannot exceed 500 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePaymentForm()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let response;
      if (showEditModal && selectedPayment) {
        // Update existing payment
        response = await apiService.updateSupplierPayment(selectedPayment.id, paymentForm);
        if (response.success) {
          toast.success('Payment updated successfully!', {
            duration: 3000,
            icon: '✅',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
          setShowEditModal(false);
        }
      } else {
        // Create new payment
        response = await apiService.createSupplierPayment(paymentForm);
        if (response.success) {
          toast.success('Payment recorded successfully!', {
            duration: 3000,
            icon: '✅',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
          setShowPaymentModal(false);
        }
      }
      
      if (response.success) {
        setSelectedPayment(null);
        setFormErrors({});
        setPaymentForm({
          supplier_id: '',
          amount: '',
          payment_method: 'cash',
          reference_number: '',
          notes: '',
          payment_date: new Date().toISOString().split('T')[0],
          paymentID: '',
          outstanding_balance: 0,
          // Payment method specific fields
          cash_breakdown: {},
          bank_name: '',
          account_name: '',
          account_number: '',
          mobile_network: '',
          sender_mobile_number: '',
          receiver_mobile_number: '',
          transaction_reference: '',
          card_number: ''
        });
        fetchPayments();
        fetchPaymentStats();
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      const action = showEditModal ? 'update' : 'record';
      toast.error(`Failed to ${action} payment. Please try again.`, {
        duration: 3000,
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setFormErrors({});
    setPaymentForm({
      supplier_id: '',
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      notes: '',
      payment_date: new Date().toISOString().split('T')[0],
      paymentID: '', // Reset payment ID
      outstanding_balance: 0, // Reset outstanding balance
      // Payment method specific fields
      cash_breakdown: {},
      bank_name: '',
      account_name: '',
      account_number: '',
      mobile_network: '',
      sender_mobile_number: '',
      receiver_mobile_number: '',
      transaction_reference: '',
      card_number: ''
    });
  };

  // Action button handlers
  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const handleEditPayment = async (payment) => {
    setSelectedPayment(payment);
    
    // Fetch current outstanding balance for the supplier
    const outstandingBalance = await fetchSupplierOutstandingBalance(payment.supplier_id);
    
    setPaymentForm({
      supplier_id: payment.supplier_id,
      amount: payment.amount,
      payment_method: payment.payment_method,
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
      payment_date: payment.payment_date,
      paymentID: payment.paymentID || '', // Use existing payment ID from database
      outstanding_balance: typeof outstandingBalance === 'number' ? outstandingBalance : 0, // Current outstanding balance
      // Payment method specific fields
      cash_breakdown: payment.cash_breakdown || {},
      bank_name: payment.bank_name || '',
      account_name: payment.account_name || '',
      account_number: payment.account_number || '',
      mobile_network: payment.mobile_network || '',
      sender_mobile_number: payment.sender_mobile_number || '',
      receiver_mobile_number: payment.receiver_mobile_number || '',
      transaction_reference: payment.transaction_reference || '',
      card_number: payment.card_number || ''
    });
    setShowEditModal(true);
  };

  const handleDeletePayment = (payment) => {
    setSelectedPayment(payment);
    setShowDeleteModal(true);
  };

  const handlePrintReceipt = (payment) => {
    setSelectedPayment(payment);
    setShowPrintModal(true);
  };

  const handlePrintPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPrintModal(true);
  };

  const handleClosePrintModal = () => {
    setShowPrintModal(false);
    setSelectedPayment(null);
  };

  const handlePrint = () => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">{APP_NAME}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Supplier Payment Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>Payment ID:</strong> ${selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</span>
            <span><strong>Date:</strong> ${formatDate(selectedPayment.payment_date)}</span>
            <span><strong>Time:</strong> ${new Date(selectedPayment.payment_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Supplier and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Supplier Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Supplier Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${selectedPayment.supplier_name || 'Unknown Supplier'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${selectedPayment.supplier_phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${selectedPayment.supplier_email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${selectedPayment.supplier_address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Payment Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Payment Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Payment Method</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Date</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${selectedPayment.payment_method?.replace('_', ' ')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${parseFloat(selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Completed</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${formatDate(selectedPayment.payment_date)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Payment Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Balance Before Payment:</span>
              <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${currencySymbol} ${parseFloat(selectedPayment.balance_before_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Amount Paid:</span>
              <span style="font-size: 16px; font-weight: bold; color: #059669;">${currencySymbol} ${parseFloat(selectedPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">New Balance:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${currencySymbol} ${parseFloat(selectedPayment.balance_after_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        ${selectedPayment.notes ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${selectedPayment.notes}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your payment!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Supplier Payment Receipt - ${selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #333;
            }
            .receipt { 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px; 
              background: white; 
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
    setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show print notification
    toast.success('Supplier payment receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedPayment(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPayment(null);
    setFormErrors({});
    setPaymentForm({
      supplier_id: '',
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      notes: '',
      payment_date: new Date().toISOString().split('T')[0],
      // Payment method specific fields
      cash_breakdown: {},
      bank_name: '',
      account_name: '',
      account_number: '',
      mobile_network: '',
      sender_mobile_number: '',
      receiver_mobile_number: '',
      transaction_reference: '',
      card_number: ''
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedPayment(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPayment) return;
    
    setDeleteLoading(true);
    
    try {
      const response = await apiService.deleteSupplierPayment(selectedPayment.id);
      if (response.success) {
        toast.success('Payment deleted successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
        setShowDeleteModal(false);
        setSelectedPayment(null);
        fetchPayments();
        fetchPaymentStats();
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment. Please try again.', {
        duration: 3000,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Status and method badges
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending', icon: Clock },
      completed: { color: 'bg-green-100 text-green-800', text: 'Completed', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', text: 'Failed', icon: XCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800', text: 'Cancelled', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      cash: { color: 'bg-green-100 text-green-800', text: 'Cash' },
      bank_transfer: { color: 'bg-blue-100 text-blue-800', text: 'Bank Transfer' },
      cheque: { color: 'bg-purple-100 text-purple-800', text: 'Cheque' },
      mobile_money: { color: 'bg-orange-100 text-orange-800', text: 'Mobile Money' },
      card: { color: 'bg-indigo-100 text-indigo-800', text: 'Card' }
    };
    
    const config = methodConfig[method] || methodConfig.cash;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="bg-white rounded-2xl mt-20 shadow-xl border border-gray-200 p-8 mb-8">
              <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                <CreditCard className="h-8 w-8 text-white" />
                    </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                  Supplier Payments
                    </h1>
                <p className="text-gray-600 text-lg">Manage supplier payments and transactions</p>
                  </div>
                </div>
            <div className="flex items-center gap-4">
                <button
                onClick={() => {
                  setError(null);
                  fetchPayments();
                }}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button 
                onClick={() => navigate('/suppliers')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                <Building2 size={20} />
                Suppliers
              </button>
              <button 
                onClick={handleOpenPaymentModal}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Plus size={20} />
                Add Payment
                </button>
              </div>
            </div>

        {/* Error Display */}
        {error && (
          <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                          </div>
                          </div>
              <div className="ml-4">
                <h3 className="text-lg font-bold text-red-800">Error</h3>
                <div className="mt-1 text-red-700">
                  <p>{error}</p>
                        </div>
                        </div>
                              </div>
                        </div>
                      )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">Total Payments</p>
                <p className="text-3xl font-bold text-blue-900">
                  {loading ? <Loader2 className="animate-spin" size={24} /> : payments.length}
                </p>
                    </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <CreditCard className="h-6 w-6 text-white" />
                      </div>
                      </div>
                </div>
          
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-green-900">
                  {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0))}
                </p>
                  </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <DollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
          
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 mb-1">Active Suppliers</p>
                <p className="text-3xl font-bold text-orange-900">
                  {loading ? <Loader2 className="animate-spin" size={24} /> : suppliers.filter(s => s.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <Building2 className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 mb-1">This Month</p>
                <p className="text-3xl font-bold text-purple-900">
                  {loading ? <Loader2 className="animate-spin" size={24} /> : payments.filter(p => {
                    const paymentDate = new Date(p.payment_date);
                    const now = new Date();
                    return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Filter className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Filters & Search</h2>
              <p className="text-gray-600">Find and filter supplier payments</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Search payments..." 
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              />
            </div>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select 
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="card">Card</option>
            </select>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="date">Sort by Date</option>
              <option value="amount">Sort by Amount</option>
              <option value="supplier">Sort by Supplier</option>
              <option value="status">Sort by Status</option>
            </select>
            <select 
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value={10}>10 per page</option>
              <option value={15}>15 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2">
              <Filter size={16} />
              Apply Filters
                </button>
                </div>
              </div>

        {/* Add Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Plus size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Add New Payment</h2>
                      <p className="text-white text-opacity-80 text-sm">Create a new supplier payment</p>
                    </div>
            </div>
                  <div className="flex items-center space-x-3">
                      <button 
                      type="button"
                      onClick={handleClosePaymentModal}
                      className="px-5 py-2 text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl font-medium"
                    >
                      Cancel
                  </button>
                      <button 
                      type="submit"
                      form="add-supplier-payment-form"
                      disabled={formLoading}
                      className="px-6 py-2 bg-white text-blue-600 rounded-xl hover:bg-gray-100 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Create Payment
                        </>
                      )}
                      </button>
                    </div>
                </div>
              </div>

              <form id="add-supplier-payment-form" onSubmit={handlePaymentSubmit} className="overflow-y-auto max-h-[calc(95vh-140px)]">
                <div className="p-6 space-y-6">
                  {/* Payment Information Section */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                      Payment Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Supplier *
                        </label>
                        <select
                          value={paymentForm.supplier_id}
                          onChange={(e) => handlePaymentFormChange('supplier_id', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                            formErrors.supplier_id ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Supplier</option>
                          {suppliers.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </option>
                          ))}
                        </select>
                        {formErrors.supplier_id && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.supplier_id}</p>
                  )}
                </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentForm.amount}
                          onChange={(e) => handlePaymentFormChange('amount', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                            formErrors.amount ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                        />
                        {formErrors.amount && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                        )}
              </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method *
                        </label>
                        <select
                          value={paymentForm.payment_method}
                          onChange={(e) => handlePaymentFormChange('payment_method', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                            formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Method</option>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                          <option value="mobile_money">Mobile Money</option>
                          <option value="card">Card</option>
                        </select>
                        {formErrors.payment_method && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>
                        )}
            </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={(e) => handlePaymentFormChange('payment_date', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                            formErrors.payment_date ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.payment_date && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_date}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reference
                        </label>
                        <input
                          type="text"
                          value={paymentForm.reference}
                          onChange={(e) => handlePaymentFormChange('reference', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Payment reference"
                        />
                        </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={paymentForm.status}
                          onChange={(e) => handlePaymentFormChange('status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </select>
                        </div>
                      </div>
                  </div>

                  {/* Additional Information Section */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-blue-600" />
                      Additional Information
                    </h3>
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Any additional notes about this payment..."
                      />
                          </div>
                          </div>
                        </div>
              </form>
                      </div>
                    </div>
        )}

        {/* View Payment Modal */}
        {showViewModal && selectedPayment && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="relative overflow-hidden bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                      <Eye size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Payment Details</h2>
                      <p className="text-white/90 text-sm font-medium">{selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</p>
                    </div>
                  </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => {
                          handleCloseViewModal();
                          setShowPrintModal(true);
                        }}
                        className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 transform hover:scale-105"
                      >
                        <Printer size={16} />
                        Print Receipt
                      </button>
                      <button 
                      onClick={handleCloseViewModal}
                      className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                      Close
                      </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Payment Information */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <CreditCard size={20} className="text-green-600" />
                      Payment Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Payment ID</label>
                        <p className="text-lg font-bold text-gray-900 mt-1">{selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</p>
                      </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Amount</label>
                          <p className="text-2xl font-bold text-gray-900 mt-1">
                            {formatCurrency(selectedPayment.amount || 0)}
                          </p>
                        </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Payment Method</label>
                        <div className="mt-1">
                          {getPaymentMethodBadge(selectedPayment.payment_method)}
                    </div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                        <div className="mt-1">
                          {getStatusBadge(selectedPayment.status)}
                        </div>
                    </div>
                  </div>
                </div>

                  {/* Supplier Information */}
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Building2 size={20} className="text-blue-600" />
                      Supplier Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Supplier Name</label>
                        <p className="text-lg font-bold text-gray-900 mt-1">{selectedPayment.supplier_name || 'Unknown Supplier'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                        <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                          <Mail size={16} className="text-gray-400" />
                          {selectedPayment.supplier_email || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                        <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                          <Phone size={16} className="text-gray-400" />
                          {selectedPayment.supplier_phone || 'N/A'}
                        </p>
                    </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Reference</label>
                        <p className="text-gray-900 font-medium mt-1">{selectedPayment.reference_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                  {/* Payment Details */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Calendar size={20} className="text-purple-600" />
                      Payment Details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Payment Date</label>
                        <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          {formatDate(selectedPayment.payment_date)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Created Date</label>
                        <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          {selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Last Updated</label>
                        <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          {selectedPayment.updated_at ? new Date(selectedPayment.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </p>
                    </div>
                  </div>
                </div>

                  {/* Additional Information */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <FileText size={20} className="text-orange-600" />
                      Additional Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Notes</label>
                        <p className="text-gray-900 font-medium mt-1 bg-white p-3 rounded-lg border border-gray-200">
                          {selectedPayment.notes || 'No notes available'}
                        </p>
                      </div>
                      </div>
                    </div>
                      </div>
                    </div>
                  </div>
                </div>
        )}

        {/* Edit Payment Modal */}
        {showEditModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Edit size={20} className="text-white" />
                  </div>
                  <div>
                      <h2 className="text-xl font-bold">Edit Payment</h2>
                      <p className="text-white text-opacity-80 text-sm">Update payment information</p>
                  </div>
                </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCloseEditModal}
                      className="px-6 py-2.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      form="edit-payment-form"
                      disabled={formLoading}
                      className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-blue-600 hover:from-red-600 hover:to-blue-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Edit className="h-4 w-4" />
                          Update Payment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <form id="edit-payment-form" onSubmit={handlePaymentSubmit} className="overflow-y-auto max-h-[calc(95vh-100px)]">
                <div className="p-6 space-y-6">
                  {/* Payment Information Section */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                      Payment Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Supplier *
                        </label>
                        <select
                          value={paymentForm.supplier_id}
                          onChange={(e) => handlePaymentFormChange('supplier_id', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.supplier_id ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Supplier</option>
                          {suppliers.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </option>
                          ))}
                        </select>
                        {formErrors.supplier_id && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.supplier_id}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount *
                        </label>
                    <input 
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentForm.amount}
                          onChange={(e) => handlePaymentFormChange('amount', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.amount ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                        />
                        {formErrors.amount && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                        )}
                  </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method *
                        </label>
                  <select 
                          value={paymentForm.payment_method}
                          onChange={(e) => handlePaymentFormChange('payment_method', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Method</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="card">Card</option>
                  </select>
                        {formErrors.payment_method && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={(e) => handlePaymentFormChange('payment_date', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.payment_date ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.payment_date && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_date}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reference
                        </label>
                        <input
                          type="text"
                          value={paymentForm.reference}
                          onChange={(e) => handlePaymentFormChange('reference', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Payment reference"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                  <select 
                          value={paymentForm.status}
                          onChange={(e) => handlePaymentFormChange('status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                  </select>
                      </div>
                </div>
              </div>

                  {/* Additional Information Section */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-blue-600" />
                      Additional Information
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Any additional notes about this payment..."
                      />
                    </div>
                </div>
              
                  </div>
              </form>
            </div>
          </div>
        )}

        {/* Print Payment Modal */}
        {showPrintModal && selectedPayment && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="relative overflow-hidden bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                      <Printer size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Print Payment Receipt</h2>
                      <p className="text-white/90 text-sm font-medium">{selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleClosePrintModal}
                      className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                    <button
                      onClick={handlePrint}
                      className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-red-600 hover:to-blue-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      Print Receipt
                    </button>
                  </div>
                  </div>
              </div>

              {/* Receipt Content */}
              <div className="p-8 max-h-[calc(90vh-120px)] overflow-y-auto">
                <div id="receipt-content" className="space-y-8">
                  {/* Header Section */}
                  <div className="space-y-6">
                    {/* Receipt Header */}
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">{APP_NAME}</h1>
                      <p className="text-gray-600 font-medium">Supplier Payment Receipt</p>
                      <div className="flex justify-between items-center text-sm text-gray-600 mt-4">
                        <span><strong>Payment ID:</strong> {selectedPayment.paymentID || `PAY-${selectedPayment.id}`}</span>
                        <span><strong>Date:</strong> {formatDate(selectedPayment.payment_date)}</span>
                        <span><strong>Time:</strong> {new Date(selectedPayment.payment_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        <span><strong>Processed by:</strong> {user?.name || 'Agent'}</span>
                      </div>
                    </div>

                    {/* Supplier and Company Section */}
                    <div className="flex gap-6">
                      {/* Supplier Section (Left) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Supplier Details</h3>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{selectedPayment.supplier_name || 'Unknown Supplier'}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{selectedPayment.supplier_phone || 'N/A'}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{selectedPayment.supplier_email || 'N/A'}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span>{selectedPayment.supplier_address || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Company Section (Right) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-green-600" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Company Details</h3>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{companySettings.company_name}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span>{companySettings.company_address}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{companySettings.company_phone}</span>
                          </div>
                          {companySettings.tax_number && (
                            <div className="text-gray-600 flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span>Tax ID: {companySettings.tax_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                      <span>Payment Information</span>
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-100 to-gray-50">
                            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-l-lg">Payment Method</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Amount</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700 rounded-r-lg">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border border-gray-200 px-4 py-3 text-sm text-gray-800 font-medium">
                              <div className="flex items-center space-x-2">
                                {selectedPayment.payment_method === 'cash' && <DollarSign size={16} className="text-green-600" />}
                                {selectedPayment.payment_method === 'mobile_money' && <Phone size={16} className="text-purple-600" />}
                                {selectedPayment.payment_method === 'bank_transfer' && <CreditCard size={16} className="text-blue-600" />}
                                {selectedPayment.payment_method === 'card' && <CreditCard size={16} className="text-indigo-600" />}
                                <span className="capitalize">{selectedPayment.payment_method?.replace('_', ' ')}</span>
                              </div>
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800 font-semibold">
                              {formatCurrency(selectedPayment.amount || 0)}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Completed
                              </span>
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800">
                              {formatDate(selectedPayment.payment_date)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Balance Summary */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <DollarSign className="w-5 h-5 text-gray-600" />
                      <span>Payment Summary</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Balance Before Payment:</span>
                          <span className="font-semibold text-gray-800">{formatCurrency(selectedPayment.balance_before_payment || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount Paid:</span>
                          <span className="font-semibold text-red-600">-{formatCurrency(selectedPayment.amount || 0)}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Balance After Payment:</span>
                          <span className="font-semibold text-green-600">{formatCurrency(selectedPayment.balance_after_payment || 0)}</span>
                        </div>
                        {selectedPayment.reference_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Reference:</span>
                            <span className="font-semibold text-gray-800">{selectedPayment.reference_number}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {selectedPayment.notes && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                      <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-gray-600" />
                        <span>Notes</span>
                      </h3>
                      <p className="text-gray-700 bg-white p-4 rounded-lg border border-gray-200">{selectedPayment.notes}</p>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-center text-gray-500 text-sm border-t border-gray-200 pt-6">
                    <p className="mb-2">Thank you for your payment!</p>
                    <p className="mb-2">Keep this receipt for your records</p>
                    <p className="font-semibold">Powered By SherSoft Technologies - 0548104944</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Table */}
        {!loading && !error && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Supplier
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Balance Before
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Amount Paid
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Balance After
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Method
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {payments.map((payment) => (
                            <tr key={payment.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-blue-100 rounded-full flex items-center justify-center">
                                    <Building2 className="h-5 w-5 text-red-600" />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {payment.supplier_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {payment.supplier_email}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900">
                                  {formatCurrency(payment.balance_before_payment || 0)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-red-600">
                                  {formatCurrency(payment.amount)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-green-600">
                                  {formatCurrency(payment.balance_after_payment || 0)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getPaymentMethodBadge(payment.payment_method)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(payment.payment_date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-2">
                                  <button 
                                    onClick={() => handleViewPayment(payment)}
                                    className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                    title="View Payment Details"
                                  >
                                    <Eye size={18} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      View
                                    </span>
                                  </button>
                                  <button 
                                    onClick={() => handleEditPayment(payment)}
                                    className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                    title="Edit Payment"
                                  >
                                    <Edit size={18} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      Edit
                                    </span>
                                  </button>
                                  <button 
                                    onClick={() => handlePrintReceipt(payment)}
                                    className="group/btn relative p-2 text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                    title="Print Receipt"
                                  >
                                    <Printer size={18} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      Receipt
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-semibold text-gray-900">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                            <span className="font-semibold text-gray-900">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                            <span className="font-semibold text-gray-900">{pagination.totalItems}</span> payments
                          </p>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                              disabled={pagination.currentPage === 1}
                              className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                              Previous
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl font-medium">
                              Page {pagination.currentPage} of {pagination.totalPages}
                            </span>
                            <button
                              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
              )}
            </div>
        )}

              {/* Record Payment Modal */}
              {showPaymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Plus size={20} className="text-white" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">Record Payment</h2>
                            <p className="text-white text-opacity-80 text-sm">Record a new supplier payment</p>
                          </div>
                        </div>
                        <button
                          onClick={handleClosePaymentModal}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <form onSubmit={handlePaymentSubmit} className="p-6 space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment ID
                        </label>
                        <input
                          type="text"
                          value={paymentForm.paymentID}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                          placeholder="Auto-generated"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Supplier *
                        </label>
                        <select
                          value={paymentForm.supplier_id}
                          onChange={(e) => handlePaymentFormChange('supplier_id', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                            formErrors.supplier_id ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                            <option value="">Select a supplier</option>
                            {suppliers.map((supplier) => (
                              <option key={supplier.id} value={supplier.id}>
                                {supplier.name}
                              </option>
                            ))}
                        </select>
                        {formErrors.supplier_id && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.supplier_id}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Outstanding Balance
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">{currencySymbol}</span>
                          <input
                            type="text"
                            value={(paymentForm.outstanding_balance || 0).toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                            readOnly
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                            placeholder="0.00"
                          />
                        </div>
                        {(paymentForm.outstanding_balance || 0) > 0 && (() => {
                          const remainingBalance = (paymentForm.outstanding_balance || 0) - (parseFloat(paymentForm.amount) || 0);
                          const isOverpaid = remainingBalance < 0;
                          const isFullyPaid = remainingBalance === 0;
                          
                          let textColor = 'text-red-600'; // Default: still has outstanding balance
                          let message = `${currencySymbol} ${Math.max(0, remainingBalance).toFixed(2)} remaining`;
                          
                          if (isFullyPaid) {
                            textColor = 'text-green-600';
                            message = `${currencySymbol} 0.00 remaining`;
                          } else if (isOverpaid) {
                            textColor = 'text-blue-600';
                            message = `${currencySymbol} ${Math.abs(remainingBalance).toFixed(2)} overpaid`;
                          }
                          
                          return (
                            <p className={`mt-1 text-sm ${textColor}`}>
                              Supplier has outstanding balance ({message})
                            </p>
                          );
                        })()}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount ({currencySymbol}) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentForm.amount}
                          onChange={(e) => handlePaymentFormChange('amount', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                            formErrors.amount ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                        />
                        {formErrors.amount && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method *
                        </label>
                        <select
                          value={paymentForm.payment_method}
                          onChange={(e) => handlePaymentFormChange('payment_method', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                            formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                          <option value="mobile_money">Mobile Money</option>
                          <option value="card">Card</option>
                        </select>
                        {formErrors.payment_method && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={(e) => handlePaymentFormChange('payment_date', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                            formErrors.payment_date ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {formErrors.payment_date && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.payment_date}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Supplier's Invoice Number
                        </label>
                        <input
                          type="text"
                          value={paymentForm.reference_number}
                          onChange={(e) => handlePaymentFormChange('reference_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          placeholder="Reference (optional)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes
                        </label>
                        <textarea
                          value={paymentForm.notes}
                          onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          placeholder="Notes (optional)"
                        />
                      </div>
                    </div>

                    {/* Dynamic Payment Method Fields */}
                    <div className="mt-6">
                      {/* Cash Breakdown */}
                      {paymentForm.payment_method === 'cash' && (
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">₵</span>
                            </span>
                            Cash Breakdown
                          </h3>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => (
                              <div key={denomination} className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="text-center">
                                  <span className="text-sm font-medium text-gray-700 block mb-2">₵{denomination}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={paymentForm.cash_breakdown[`${denomination}_notes`] || 0}
                                    onChange={(e) => updateCashBreakdown(denomination, e.target.value)}
                                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  />
                                  <span className="text-xs font-medium text-gray-900 block mt-1">
                                    ₵{((paymentForm.cash_breakdown[`${denomination}_notes`] || 0) * denomination).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-300">
                            <div className="text-center">
                              <span className="text-lg font-bold text-green-600">
                                Total: ₵{Object.keys(paymentForm.cash_breakdown).reduce((total, key) => {
                                  const denomination = parseInt(key.split('_')[0]);
                                  const count = paymentForm.cash_breakdown[key] || 0;
                                  return total + (denomination * count);
                                }, 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bank Transfer Fields */}
                      {paymentForm.payment_method === 'bank_transfer' && (
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">🏦</span>
                            </span>
                            Bank Transfer Details
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Bank Name
                              </label>
                              <input
                                type="text"
                                value={paymentForm.bank_name}
                                onChange={(e) => handlePaymentFormChange('bank_name', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter bank name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Account Name
                              </label>
                              <input
                                type="text"
                                value={paymentForm.account_name}
                                onChange={(e) => handlePaymentFormChange('account_name', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter account name"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Account Number
                              </label>
                              <input
                                type="text"
                                value={paymentForm.account_number}
                                onChange={(e) => handlePaymentFormChange('account_number', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter account number"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mobile Money Fields */}
                      {paymentForm.payment_method === 'mobile_money' && (
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">📱</span>
                            </span>
                            Mobile Money Details
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mobile Network
                              </label>
                              <select
                                value={paymentForm.mobile_network}
                                onChange={(e) => handlePaymentFormChange('mobile_network', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                              >
                                <option value="">Select Network</option>
                                <option value="mtn">MTN</option>
                                <option value="vodafone">Vodafone</option>
                                <option value="airteltigo">AirtelTigo</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Sender Mobile Number
                              </label>
                              <input
                                type="tel"
                                value={paymentForm.sender_mobile_number}
                                onChange={(e) => handlePaymentFormChange('sender_mobile_number', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter sender number"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Receiver Mobile Number
                              </label>
                              <input
                                type="tel"
                                value={paymentForm.receiver_mobile_number}
                                onChange={(e) => handlePaymentFormChange('receiver_mobile_number', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter receiver number"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Transaction Reference
                              </label>
                              <input
                                type="text"
                                value={paymentForm.transaction_reference}
                                onChange={(e) => handlePaymentFormChange('transaction_reference', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter transaction reference"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Card Payment Fields */}
                      {paymentForm.payment_method === 'card' && (
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">💳</span>
                            </span>
                            Card Payment Details
                          </h3>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Card Number (Last 4 digits)
                            </label>
                            <input
                              type="text"
                              value={paymentForm.card_number}
                              onChange={(e) => handlePaymentFormChange('card_number', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                              placeholder="Enter last 4 digits of card"
                              maxLength="4"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleClosePaymentModal}
                        className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={formLoading}
                        className="px-6 py-3 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-xl hover:from-red-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
                      >
                        {formLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Recording...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" />
                            Record Payment
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              )}


              {/* Edit Payment Modal */}
              {showEditModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Edit size={20} className="text-white" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">Edit Payment</h2>
                            <p className="text-white text-opacity-80 text-sm">Update payment information</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                        <button
                            type="button"
                          onClick={handleCloseEditModal}
                            className="px-6 py-2.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                          >
                            <X size={16} />
                            Cancel
                          </button>
                          <button
                            type="submit"
                            form="edit-payment-form-2"
                            disabled={formLoading}
                            className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-blue-600 hover:from-red-600 hover:to-blue-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {formLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              <>
                                <Edit className="h-4 w-4" />
                                Update Payment
                              </>
                            )}
                        </button>
                        </div>
                      </div>
                    </div>

                    <form id="edit-payment-form-2" onSubmit={handlePaymentSubmit} className="p-6 space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment ID
                          </label>
                          <input
                            type="text"
                            value={paymentForm.paymentID}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                            placeholder="Auto-generated"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Supplier *
                          </label>
                          <select
                            value={paymentForm.supplier_id}
                            onChange={(e) => handlePaymentFormChange('supplier_id', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                              formErrors.supplier_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                          >
                            <option value="">Select a supplier</option>
                            {suppliers.map((supplier) => (
                              <option key={supplier.id} value={supplier.id}>
                                {supplier.name}
                              </option>
                            ))}
                          </select>
                          {formErrors.supplier_id && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.supplier_id}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Outstanding Balance
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₵</span>
                            <input
                              type="text"
                              value={(paymentForm.outstanding_balance || 0).toFixed(2)}
                              readOnly
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                              placeholder="0.00"
                            />
                          </div>
                          {(paymentForm.outstanding_balance || 0) > 0 && (() => {
                            const remainingBalance = (paymentForm.outstanding_balance || 0) - (parseFloat(paymentForm.amount) || 0);
                            const isOverpaid = remainingBalance < 0;
                            const isFullyPaid = remainingBalance === 0;
                            
                            let textColor = 'text-red-600'; // Default: still has outstanding balance
                            let message = `₵${Math.max(0, remainingBalance).toFixed(2)} remaining`;
                            
                            if (isFullyPaid) {
                              textColor = 'text-green-600';
                              message = '₵0.00 remaining';
                            } else if (isOverpaid) {
                              textColor = 'text-blue-600';
                              message = `₵${Math.abs(remainingBalance).toFixed(2)} overpaid`;
                            }
                            
                            return (
                              <p className={`mt-1 text-sm ${textColor}`}>
                                Supplier has outstanding balance ({message})
                              </p>
                            );
                          })()}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount (₵) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={paymentForm.amount}
                            onChange={(e) => handlePaymentFormChange('amount', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                              formErrors.amount ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="0.00"
                          />
                          {formErrors.amount && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Method *
                          </label>
                          <select
                            value={paymentForm.payment_method}
                            onChange={(e) => handlePaymentFormChange('payment_method', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                              formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                            }`}
                          >
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cheque">Cheque</option>
                            <option value="mobile_money">Mobile Money</option>
                            <option value="card">Card</option>
                          </select>
                          {formErrors.payment_method && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Date *
                          </label>
                          <input
                            type="date"
                            value={paymentForm.payment_date}
                            onChange={(e) => handlePaymentFormChange('payment_date', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                              formErrors.payment_date ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {formErrors.payment_date && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.payment_date}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Number
                          </label>
                          <input
                            type="text"
                            value={paymentForm.reference_number}
                            onChange={(e) => handlePaymentFormChange('reference_number', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Reference (optional)"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notes
                          </label>
                          <textarea
                            value={paymentForm.notes}
                            onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Notes (optional)"
                          />
                        </div>
                      </div>

                      {/* Dynamic Payment Method Fields */}
                      <div className="mt-4">
                        {/* Cash Breakdown */}
                        {paymentForm.payment_method === 'cash' && (
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">₵</span>
                              </span>
                              Cash Breakdown
                            </h3>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                              {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => (
                                <div key={denomination} className="bg-white rounded-lg p-3 border border-gray-200">
                                  <div className="text-center">
                                    <span className="text-sm font-medium text-gray-700 block mb-2">₵{denomination}</span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={paymentForm.cash_breakdown[`${denomination}_notes`] || 0}
                                      onChange={(e) => updateCashBreakdown(denomination, e.target.value)}
                                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                    />
                                    <span className="text-xs font-medium text-gray-900 block mt-1">
                                      ₵{((paymentForm.cash_breakdown[`${denomination}_notes`] || 0) * denomination).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-300">
                              <div className="text-center">
                                <span className="text-lg font-bold text-green-600">
                                  Total: ₵{Object.keys(paymentForm.cash_breakdown).reduce((total, key) => {
                                    const denomination = parseInt(key.split('_')[0]);
                                    const count = paymentForm.cash_breakdown[key] || 0;
                                    return total + (denomination * count);
                                  }, 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Bank Transfer Fields */}
                        {paymentForm.payment_method === 'bank_transfer' && (
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">🏦</span>
                              </span>
                              Bank Transfer Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Bank Name
                                </label>
                                <input
                                  type="text"
                                  value={paymentForm.bank_name}
                                  onChange={(e) => handlePaymentFormChange('bank_name', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter bank name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Account Name
                                </label>
                                <input
                                  type="text"
                                  value={paymentForm.account_name}
                                  onChange={(e) => handlePaymentFormChange('account_name', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter account name"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Account Number
                                </label>
                                <input
                                  type="text"
                                  value={paymentForm.account_number}
                                  onChange={(e) => handlePaymentFormChange('account_number', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter account number"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Mobile Money Fields */}
                        {paymentForm.payment_method === 'mobile_money' && (
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">📱</span>
                              </span>
                              Mobile Money Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Mobile Network
                                </label>
                                <select
                                  value={paymentForm.mobile_network}
                                  onChange={(e) => handlePaymentFormChange('mobile_network', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                >
                                  <option value="">Select Network</option>
                                  <option value="mtn">MTN</option>
                                  <option value="vodafone">Vodafone</option>
                                  <option value="airteltigo">AirtelTigo</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Sender Mobile Number
                                </label>
                                <input
                                  type="tel"
                                  value={paymentForm.sender_mobile_number}
                                  onChange={(e) => handlePaymentFormChange('sender_mobile_number', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter sender number"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Receiver Mobile Number
                                </label>
                                <input
                                  type="tel"
                                  value={paymentForm.receiver_mobile_number}
                                  onChange={(e) => handlePaymentFormChange('receiver_mobile_number', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter receiver number"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Transaction Reference
                                </label>
                                <input
                                  type="text"
                                  value={paymentForm.transaction_reference}
                                  onChange={(e) => handlePaymentFormChange('transaction_reference', e.target.value)}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                  placeholder="Enter transaction reference"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Card Payment Fields */}
                        {paymentForm.payment_method === 'card' && (
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">💳</span>
                              </span>
                              Card Payment Details
                            </h3>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Card Number (Last 4 digits)
                              </label>
                              <input
                                type="text"
                                value={paymentForm.card_number}
                                onChange={(e) => handlePaymentFormChange('card_number', e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                placeholder="Enter last 4 digits of card"
                                maxLength="4"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                    </form>
                  </div>
                </div>
              )}

              {/* Delete Payment Modal */}
              {showDeleteModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Trash2 size={20} className="text-white" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">Delete Payment</h2>
                            <p className="text-white text-opacity-80 text-sm">This action cannot be undone</p>
                          </div>
                        </div>
                        <button
                          onClick={handleCloseDeleteModal}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Trash2 className="h-8 w-8 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure?</h3>
                        <p className="text-gray-600 mb-6">
                          You are about to delete the payment of <span className="font-semibold text-red-600">{formatCurrency(selectedPayment.amount)}</span> to <span className="font-semibold">{selectedPayment.supplier_name}</span>. This action cannot be undone.
                        </p>
                      </div>

                      <div className="flex items-center justify-end space-x-4">
                        <button
                          onClick={handleCloseDeleteModal}
                          className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmDelete}
                          disabled={deleteLoading}
                          className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
                        >
                          {deleteLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4" />
                              Delete Payment
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
                          </div>
                          </div>
    </Layout>
  );
};

export default SupplierPayments;
